// File: src/pages/ProfitandLossReport.jsx
// ✅ Uses YOUR “WareHouseList-like” UI (same wh-toolbar + wh-table styles)
// ✅ Keeps the SAME functionality you gave:
//    - fetch accounts (viewAccount) and render Trading + P&L
//    - calculate Sales, Purchases, Purchase Return, Direct Expense, Opening Stock, Closing Stock
//    - auto Gross Profit/Loss balancing in Trading, then carry to P&L
//    - LedgerPdf export (prints #pl-root)
//    - date filter UI + Submit (applies local date filter on orders)
//    - click on any entry opens modal (Purchases/Sales opens grouped Dr/Cr view)

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Input,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import swal from "sweetalert";

// ---- your project APIs (adjust paths if your project differs) ----
import {
  _GetByUrl,
  _Get,
  PurchaseOrderList,
  Purchase_ReturnList,
} from "../import/ApiEndPoint/ApiCalling";

import {
  viewAccount,
  view_create_order_history,
  PurchaseProductList_Product,
  WareHouse_Closing_Stock,
} from "../import/ApiEndPoint/Api";

// your PDF component
import LedgerPdf from "./LedgerPdf";

/* ---------------- page-scoped css (same family as DeadPartyReport) ---------------- */
const injectOnce = () => {
  if (document.getElementById("pl-inline-css")) return;
  const style = document.createElement("style");
  style.id = "pl-inline-css";
  style.innerHTML = `
    .wh-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .wh-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .wh-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .wh-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:800; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:10px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .wh-table tbody td{
      padding:10px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .wh-table tbody tr:hover{ background:#f9fbff; }
    .pill-muted{
      background:#f1f5f9; border:1px solid #e5e7eb; padding:6px 10px; border-radius:999px; color:#0f172a; font-weight:700;
    }
    .pl-click{
      cursor:pointer;
      user-select:none;
    }
    .pl-click:hover{
      text-decoration: underline;
    }
  `;
  document.head.appendChild(style);
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDateOnly = (d) => {
  if (!d) return "";
  const s = String(d);
  if (s.includes("T")) return s.split("T")[0];
  try {
    return new Date(d).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const inRange = (dateStr, start, end) => {
  const d = toDateOnly(dateStr);
  if (!d) return false;
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
};

export default function ProfitandLossReport() {
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState([]);

  // raw fetched data
  const [salesOrders, setSalesOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [products, setProducts] = useState([]);

  // calculated numbers
  const [salesAmount, setSalesAmount] = useState(0);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [purchaseReturnAmount, setPurchaseReturnAmount] = useState(0);
  const [directExpense, setDirectExpense] = useState(0);
  const [openingStock, setOpeningStock] = useState(0);
  const [closingStock, setClosingStock] = useState(0);

  // date filter UI
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: "",
    endDate: "",
  });

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntryGroup, setSelectedEntryGroup] = useState(null);

  useEffect(() => injectOnce(), []);

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  }, []);

  const db = userData?.database;
  const userId = userData?._id;

  /* ---------------- load everything once ---------------- */
  useEffect(() => {
    const loadAll = async () => {
      if (!db || !userId) {
        setLoading(false);
        swal("User data missing in localStorage (userData).");
        return;
      }

      setLoading(true);
      try {
        // 1) Accounts
        const accRes = await _GetByUrl(viewAccount, db);
        setCategories(accRes?.viewAccounts?.Accounts || []);

        // 2) Sales
        const salesRes = await _Get(view_create_order_history, userId, db);
        const salesList = Array.isArray(salesRes?.orderHistory)
          ? salesRes.orderHistory
          : [];
        setSalesOrders(salesList);

        // 3) Purchases + Returns
        const purRes = await PurchaseOrderList(userId, db);
        const purList = Array.isArray(purRes?.orderHistory)
          ? purRes.orderHistory
          : [];
        setPurchaseOrders(purList);

        const purRetRes = await Purchase_ReturnList(userId, db);
        const purRetList = Array.isArray(purRetRes?.PurchaseReturn)
          ? purRetRes.PurchaseReturn
          : [];
        setPurchaseReturns(purRetList);

        // 4) Products (for opening + closing calc)
        const prodRes = await _Get(PurchaseProductList_Product, db);
        const prodList = Array.isArray(prodRes?.Product) ? prodRes.Product : [];
        setProducts(prodList);

        // 5) Opening stock value
        const opTotal = prodList.reduce((acc, p) => {
          const stock = toNum(p?.Opening_Stock);
          const rate = toNum(p?.openingRate);
          return acc + stock * rate;
        }, 0);
        setOpeningStock(Number(opTotal.toFixed(2)));

        // 6) Closing stock value (warehouse closing if present, else fallback)
        //    We keep your “avg purchase rate * currentStock” logic, but fixed & safer.
        const closeVal = await computeClosingStockValue({
          db,
          products: prodList,
          purchaseOrders: purList,
        });
        setClosingStock(Number(closeVal.toFixed(2)));
      } catch (e) {
        console.error("[ProfitandLossReport] load error:", e);
        swal(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load Profit & Loss data",
        );
      } finally {
        setLoading(false);
      }
    };

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, userId]);

  /* ---------------- recompute totals when Submit is clicked ---------------- */
  useEffect(() => {
    // Apply local filtering on already-fetched orders
    const { startDate, endDate } = appliedFilter;

    // SALES: completed only, excluding deactive, sum order.amount ONCE per order
    const salesFiltered = (salesOrders || [])
      .filter((o) => (o?.status || "").toLowerCase() !== "deactive")
      .filter((o) => (o?.status || "").toLowerCase() === "completed")
      .filter((o) =>
        inRange(o?.date || o?.updatedAt || o?.createdAt, startDate, endDate),
      );

    const salesTotal = salesFiltered.reduce(
      (acc, o) => acc + toNum(o?.amount),
      0,
    );
    setSalesAmount(Number(salesTotal.toFixed(2)));

    // PURCHASE: completed only, sum order.amount once per order
    const purchaseCompleted = (purchaseOrders || [])
      .filter((o) => (o?.status || "").toLowerCase() === "completed")
      .filter((o) =>
        inRange(o?.date || o?.updatedAt || o?.createdAt, startDate, endDate),
      );

    const purchaseGross = purchaseCompleted.reduce(
      (acc, o) => acc + toNum(o?.amount),
      0,
    );

    // DIRECT EXPENSE: sum extra charges once per order (not per item)
    const expenseTotal = purchaseCompleted.reduce((acc, o) => {
      const extra =
        toNum(o?.coolieAndCartage) +
        toNum(o?.labourCost) +
        toNum(o?.localFreight) +
        toNum(o?.miscellaneousCost) +
        toNum(o?.shippingCost) +
        toNum(o?.transportationCost) +
        toNum(o?.tax);
      return acc + extra;
    }, 0);
    setDirectExpense(Number(expenseTotal.toFixed(2)));

    // PURCHASE RETURN: sum orderId.amount in date range
    const returnTotal = (purchaseReturns || [])
      .filter((r) =>
        inRange(r?.date || r?.updatedAt || r?.createdAt, startDate, endDate),
      )
      .reduce((acc, r) => acc + toNum(r?.orderId?.amount), 0);

    setPurchaseReturnAmount(Number(returnTotal.toFixed(2)));

    // NET PURCHASE = purchases - returns
    const netPurchase = purchaseGross - returnTotal;
    setPurchaseAmount(Number(netPurchase.toFixed(2)));
  }, [appliedFilter, salesOrders, purchaseOrders, purchaseReturns]);

  /* ---------------- handlers ---------------- */
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateFilter((p) => ({ ...p, [name]: value }));
  };

  const handleSubmitDate = (e) => {
    e.preventDefault();
    setAppliedFilter({
      startDate: dateFilter?.startDate || "",
      endDate: dateFilter?.endDate || "",
    });
  };

  const handleEntryClick = (entry) => {
    if (!entry || !entry.ledgerName) return;

    let relatedEntries = [];

    if (entry.ledgerName === "Purchases") {
      relatedEntries = [
        { ledgerName: "Purchases", side: "Debit", amount: purchaseAmount },
        {
          ledgerName: "Purchase Return",
          side: "Credit",
          amount: purchaseReturnAmount,
        },
      ];
    } else if (entry.ledgerName === "Sales Account") {
      relatedEntries = [
        { ledgerName: "Sales Account", side: "Credit", amount: salesAmount },
        { ledgerName: "Sales Return", side: "Debit", amount: 0 },
      ];
    } else {
      relatedEntries = [entry];
    }

    setSelectedEntryGroup(relatedEntries);
    setModalOpen(true);
  };

  /* ---------------- build statement data ---------------- */
  const buildTwoSide = (
    accountType,
    leftSide,
    rightSide,
    grossProfitCarry = 0,
    grossLossCarry = 0,
  ) => {
    const leftData = (categories || []).filter(
      (it) =>
        (it?.accountType || "").includes(accountType) && it?.side === leftSide,
    );

    const rightData = (categories || []).filter(
      (it) =>
        (it?.accountType || "").includes(accountType) && it?.side === rightSide,
    );

    const updatedLeft = leftData.map((it) => {
      if (it?.ledgerName === "Purchases")
        return { ...it, amount: purchaseAmount, side: leftSide };
      if (it?.ledgerName === "Purchase Return")
        return { ...it, amount: purchaseReturnAmount, side: leftSide };
      if (it?.ledgerName === "Opening Stock")
        return { ...it, amount: openingStock, side: leftSide };
      if (it?.ledgerName === "Direct Expenses")
        return { ...it, amount: directExpense, side: leftSide };
      return it;
    });

    const updatedRight = rightData.map((it) => {
      if (it?.ledgerName === "Sales Account")
        return { ...it, amount: salesAmount, side: rightSide };
      if (it?.ledgerName === "Closing Stock")
        return { ...it, amount: closingStock, side: rightSide };
      return it;
    });

    let finalLeft = [...updatedLeft];
    let finalRight = [...updatedRight];

    // totals before balancing
    let leftTotal = finalLeft.reduce((acc, cur) => acc + toNum(cur?.amount), 0);
    let rightTotal = finalRight.reduce(
      (acc, cur) => acc + toNum(cur?.amount),
      0,
    );

    let tradingGrossProfit = 0;
    let tradingGrossLoss = 0;

    // Trading: add gross profit/loss to balance
    if (accountType === "Trading Account") {
      if (leftTotal > rightTotal) {
        tradingGrossLoss = leftTotal - rightTotal;
        finalRight.push({
          ledgerName: "Gross Loss",
          amount: Number(tradingGrossLoss.toFixed(2)),
          side: rightSide,
        });
        rightTotal += tradingGrossLoss;
      } else if (rightTotal > leftTotal) {
        tradingGrossProfit = rightTotal - leftTotal;
        finalLeft.push({
          ledgerName: "Gross Profit",
          amount: Number(tradingGrossProfit.toFixed(2)),
          side: leftSide,
        });
        leftTotal += tradingGrossProfit;
      }
    }

    // P&L: carry gross profit/loss from trading
    if (accountType === "Profit & Loss Account") {
      if (grossProfitCarry > 0) {
        finalRight.push({
          ledgerName: "Gross Profit",
          amount: Number(grossProfitCarry.toFixed(2)),
          side: rightSide,
        });
        rightTotal += grossProfitCarry;
      }
      if (grossLossCarry > 0) {
        finalLeft.push({
          ledgerName: "Gross Loss",
          amount: Number(grossLossCarry.toFixed(2)),
          side: leftSide,
        });
        leftTotal += grossLossCarry;
      }

      // (Optional) Net Profit/Loss balancing if your Accounts list doesn't already include it
      if (leftTotal > rightTotal) {
        const netProfit = leftTotal - rightTotal;
        finalRight.push({
          ledgerName: "Net Profit",
          amount: Number(netProfit.toFixed(2)),
          side: rightSide,
        });
        rightTotal += netProfit;
      } else if (rightTotal > leftTotal) {
        const netLoss = rightTotal - leftTotal;
        finalLeft.push({
          ledgerName: "Net Loss",
          amount: Number(netLoss.toFixed(2)),
          side: leftSide,
        });
        leftTotal += netLoss;
      }
    }

    return {
      finalLeft,
      finalRight,
      leftTotal: Number(leftTotal.toFixed(2)),
      rightTotal: Number(rightTotal.toFixed(2)),
      tradingGrossProfit,
      tradingGrossLoss,
    };
  };

  const statement = useMemo(() => {
    const trading = buildTwoSide("Trading Account", "Debit", "Credit");
    const pnl = buildTwoSide(
      "Profit & Loss Account",
      "Expense",
      "Income",
      trading.tradingGrossProfit,
      trading.tradingGrossLoss,
    );
    return { trading, pnl };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categories,
    purchaseAmount,
    purchaseReturnAmount,
    salesAmount,
    openingStock,
    closingStock,
    directExpense,
  ]);

  const reportYearText = useMemo(() => {
    // show something sensible
    if (appliedFilter?.startDate || appliedFilter?.endDate) {
      return `${appliedFilter?.startDate || "…"} to ${appliedFilter?.endDate || "…"}`;
    }
    const y = new Date().getFullYear();
    return `For the year ending ${y}`;
  }, [appliedFilter]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  // total "records" count pill like your UI (we can show total account rows used)
  const totalRowsUsed =
    (statement?.trading?.finalLeft?.length || 0) +
    (statement?.trading?.finalRight?.length || 0) +
    (statement?.pnl?.finalLeft?.length || 0) +
    (statement?.pnl?.finalRight?.length || 0);

  return (
    <div>
      {/* Toolbar (YOUR UI) */}
      <div className="wh-toolbar" style={{ marginBottom: 10 }}>
        <Card
          style={{
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 14,
            border: "1px solid #e5e7eb",
            background: "#f4f7f6",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div
              style={{
                fontWeight: 800,
                textTransform: "uppercase",
                fontSize: 18,
              }}
            >
              Trading & Profit / Loss{" "}
              <span className="count-pill" title="rows used">
                {totalRowsUsed}
              </span>
            </div>

            <div style={{ flex: "1 1 240px" }} />

            {/* PDF button (same as you had) */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <LedgerPdf
                downloadFileName="TradingAndProfitLossReport"
                rootElementId="pl-root"
              />
            </div>

            {/* Date filters (keep same UI feel) */}
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "end",
                flexWrap: "wrap",
              }}
            >
              <div>
                <Label style={{ marginBottom: 4 }}>Start Date</Label>
                <Input
                  type="date"
                  name="startDate"
                  value={dateFilter?.startDate || ""}
                  onChange={handleDateChange}
                  style={{ background: "#fff" }}
                />
              </div>

              <div>
                <Label style={{ marginBottom: 4 }}>End Date</Label>
                <Input
                  type="date"
                  name="endDate"
                  value={dateFilter?.endDate || ""}
                  onChange={handleDateChange}
                  style={{ background: "#fff" }}
                />
              </div>

              <Button
                color="primary"
                onClick={handleSubmitDate}
                style={{ height: 38 }}
              >
                Submit
              </Button>

              <span className="pill-muted" title="Applied filter">
                {reportYearText}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Report Body */}
      <Card
        style={{
          borderRadius: 14,
          border: "1px solid #e5e7eb",
          padding: 10,
          background: "#fff",
        }}
      >
        <div id="pl-root">
          <div style={{ textAlign: "center", padding: 10 }}>
            <hr />
            <h3 style={{ color: "#0f172a", marginBottom: 6 }}>
              Trading Account and Profit and Loss Account
            </h3>
            <p style={{ color: "#64748b", marginBottom: 0 }}>
              {reportYearText}
            </p>
            <hr />
          </div>

          {/* Trading Account */}
          <div style={{ marginTop: 6 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: 16,
                color: "#0f172a",
                padding: "8px 6px",
              }}
            >
              Trading Account
            </div>

            <div className="wh-table-wrap">
              <table className="wh-table">
                <thead>
                  <tr>
                    <th>Debit</th>
                    <th>Amount</th>
                    <th>Credit</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTwoSideRows(
                    statement.trading.finalLeft,
                    statement.trading.finalRight,
                    handleEntryClick,
                  )}
                  <tr style={{ fontWeight: 900, background: "#f1f5f9" }}>
                    <td>Total</td>
                    <td>{statement.trading.leftTotal.toFixed(2)}</td>
                    <td>Total</td>
                    <td>{statement.trading.rightTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Profit & Loss Account */}
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: 16,
                color: "#0f172a",
                padding: "8px 6px",
              }}
            >
              Profit and Loss Account
            </div>

            <div className="wh-table-wrap">
              <table className="wh-table">
                <thead>
                  <tr>
                    <th>Expenses</th>
                    <th>Amount</th>
                    <th>Income</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTwoSideRows(
                    statement.pnl.finalLeft,
                    statement.pnl.finalRight,
                    handleEntryClick,
                  )}
                  <tr style={{ fontWeight: 900, background: "#f1f5f9" }}>
                    <td>Total</td>
                    <td>{statement.pnl.leftTotal.toFixed(2)}</td>
                    <td>Total</td>
                    <td>{statement.pnl.rightTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Card>

      {/* Ledger Details Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Ledger Details
        </ModalHeader>
        <ModalBody>
          {selectedEntryGroup ? (
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    width: "40%",
                    padding: "10px",
                    borderRight: "1px solid #e5e7eb",
                    textAlign: "center",
                  }}
                >
                  Particular
                </div>
                <div
                  style={{
                    width: "30%",
                    padding: "10px",
                    borderRight: "1px solid #e5e7eb",
                    textAlign: "center",
                  }}
                >
                  Amount (Dr)
                </div>
                <div
                  style={{ width: "30%", padding: "10px", textAlign: "center" }}
                >
                  Amount (Cr)
                </div>
              </div>

              {selectedEntryGroup.map((entry, index) => (
                <div
                  key={index}
                  style={{ display: "flex", borderBottom: "1px solid #e5e7eb" }}
                >
                  <div
                    style={{
                      width: "40%",
                      padding: "10px",
                      borderRight: "1px solid #e5e7eb",
                    }}
                  >
                    {entry.ledgerName}
                  </div>
                  <div
                    style={{
                      width: "30%",
                      padding: "10px",
                      borderRight: "1px solid #e5e7eb",
                      textAlign: "center",
                    }}
                  >
                    {entry.side === "Debit" || entry.side === "Expense"
                      ? `₹${toNum(entry.amount).toFixed(2)}`
                      : ""}
                  </div>
                  <div
                    style={{
                      width: "30%",
                      padding: "10px",
                      textAlign: "center",
                    }}
                  >
                    {entry.side === "Credit" || entry.side === "Income"
                      ? `₹${toNum(entry.amount).toFixed(2)}`
                      : ""}
                  </div>
                </div>
              ))}

              <div
                style={{
                  display: "flex",
                  fontWeight: 900,
                  background: "#f1f5f9",
                }}
              >
                <div
                  style={{
                    width: "40%",
                    padding: "10px",
                    borderRight: "1px solid #e5e7eb",
                  }}
                >
                  Total
                </div>
                <div
                  style={{
                    width: "30%",
                    padding: "10px",
                    borderRight: "1px solid #e5e7eb",
                    textAlign: "center",
                  }}
                >
                  ₹
                  {selectedEntryGroup
                    .filter((e) => e.side === "Debit" || e.side === "Expense")
                    .reduce((acc, cur) => acc + toNum(cur.amount), 0)
                    .toFixed(2)}
                </div>
                <div
                  style={{ width: "30%", padding: "10px", textAlign: "center" }}
                >
                  ₹
                  {selectedEntryGroup
                    .filter((e) => e.side === "Credit" || e.side === "Income")
                    .reduce((acc, cur) => acc + toNum(cur.amount), 0)
                    .toFixed(2)}
                </div>
              </div>
            </div>
          ) : (
            <p>No data selected.</p>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}

/* ---------------- render rows for 2-side table ---------------- */
function renderTwoSideRows(leftArr, rightArr, onClick) {
  const L = Array.isArray(leftArr) ? leftArr : [];
  const R = Array.isArray(rightArr) ? rightArr : [];
  const max = Math.max(L.length, R.length);

  const rows = [];
  for (let i = 0; i < max; i++) {
    const left = L[i] || {};
    const right = R[i] || {};

    rows.push(
      <tr key={i}>
        <td className="pl-click" onClick={() => onClick(left)}>
          {left?.ledgerName || ""}
        </td>
        <td className="pl-click" onClick={() => onClick(left)}>
          {left?.amount !== undefined ? toNum(left.amount).toFixed(2) : ""}
        </td>
        <td className="pl-click" onClick={() => onClick(right)}>
          {right?.ledgerName || ""}
        </td>
        <td className="pl-click" onClick={() => onClick(right)}>
          {right?.amount !== undefined ? toNum(right.amount).toFixed(2) : ""}
        </td>
      </tr>,
    );
  }
  return rows;
}

/* ---------------- closing stock calculator (fixed version of your logic) ---------------- */
async function computeClosingStockValue({ db, products, purchaseOrders }) {
  // try warehouse closing first
  try {
    const whRes = await _Get(WareHouse_Closing_Stock, db);

    // if API says not found -> fallback to opening products only
    if (whRes?.status === false || whRes?.message === "Not Found") {
      return fallbackClosingFromOpeningOnly({ products, purchaseOrders });
    }

    const wh = Array.isArray(whRes?.Warehouse) ? whRes.Warehouse : [];
    const closingWarehouses = wh.filter(
      (w) => String(w?.closingStatus || "").toLowerCase() === "closing",
    );

    // build items: productId + closing qty (pendingStock)
    const items = [];
    closingWarehouses.forEach((w) => {
      (w?.productItems || []).forEach((pi) => {
        const pid = pi?.productId?._id;
        if (!pid) return;
        items.push({
          pId: pid,
          productId: pi?.productId,
          currentStock: toNum(pi?.pendingStock), // treat pendingStock as closing qty
        });
      });
    });

    // combine by product id (sum currentStock)
    const combinedMap = {};
    items.forEach((it) => {
      if (!combinedMap[it.pId]) combinedMap[it.pId] = { ...it };
      else combinedMap[it.pId].currentStock += toNum(it.currentStock);
    });

    let combined = Object.values(combinedMap);

    // include opening products with Opening_Stock (only if missing from warehouse closing)
    const combinedIds = new Set(combined.map((x) => x.pId));
    const openingExtra = (products || [])
      .filter((p) => toNum(p?.Opening_Stock) !== 0)
      .filter((p) => !combinedIds.has(p?._id))
      .map((p) => ({
        pId: p?._id,
        productId: p,
        currentStock: toNum(p?.Opening_Stock), // fallback qty
      }));

    combined = [...combined, ...openingExtra];

    // compute avg purchase rate per product and total value
    let totalValue = 0;
    combined.forEach((row) => {
      const title = row?.productId?.Product_Title;
      const avgRate = calculateAvgPurchaseRateSafe(
        purchaseOrders,
        row?.productId,
      );
      totalValue += toNum(row?.currentStock) * toNum(avgRate);
    });

    return totalValue;
  } catch (e) {
    console.error("[computeClosingStockValue] error:", e);
    // fallback safe
    return fallbackClosingFromOpeningOnly({ products, purchaseOrders });
  }
}

function fallbackClosingFromOpeningOnly({ products, purchaseOrders }) {
  const openingProducts = (products || [])
    .filter((p) => toNum(p?.Opening_Stock) !== 0)
    .map((p) => ({
      productId: p,
      currentStock: toNum(p?.Opening_Stock),
    }));

  let totalValue = 0;
  openingProducts.forEach((row) => {
    const avgRate = calculateAvgPurchaseRateSafe(
      purchaseOrders,
      row?.productId,
    );
    totalValue += toNum(row?.currentStock) * toNum(avgRate);
  });
  return totalValue;
}

/**
 * Safer avg purchase rate:
 * - sums item totals for matching product across completed purchase orders
 * - distributes extra charges across items proportionally by itemTotal
 * - adds opening stock value once (openingRate * Opening_Stock)
 */
function calculateAvgPurchaseRateSafe(allPurchaseOrders, productObj) {
  if (!productObj) return 0;

  const title = toStr(productObj?.Product_Title).trim();
  if (!title) return 0;

  const orders = (allPurchaseOrders || []).filter(
    (o) => String(o?.status || "").toLowerCase() === "completed",
  );

  let totalQty = 0;
  let totalAmount = 0;

  orders.forEach((order) => {
    const items = Array.isArray(order?.orderItems) ? order.orderItems : [];
    if (!items.length) return;

    // match items by Product_Title
    const matching = items.filter(
      (it) => toStr(it?.productId?.Product_Title).trim() === title,
    );
    if (!matching.length) return;

    const extraCharges =
      toNum(order?.coolieAndCartage) +
      toNum(order?.labourCost) +
      toNum(order?.localFreight) +
      toNum(order?.miscellaneousCost) +
      toNum(order?.shippingCost) +
      toNum(order?.transportationCost) +
      toNum(order?.tax);

    const sumItemTotals =
      matching.reduce(
        (acc, it) => acc + toNum(it?.price) * toNum(it?.qty),
        0,
      ) || 1;

    matching.forEach((it) => {
      const qty = toNum(it?.qty);
      const itemTotal = toNum(it?.price) * qty;

      // distribute charges by share
      const share = itemTotal / sumItemTotals;
      const allocatedExtra = extraCharges * share;

      totalQty += qty;
      totalAmount += itemTotal + allocatedExtra;
    });
  });

  // add opening stock ONCE
  const openingStock = toNum(productObj?.Opening_Stock);
  const openingRate = toNum(productObj?.openingRate);
  if (openingStock > 0 && openingRate > 0) {
    totalQty += openingStock;
    totalAmount += openingStock * openingRate;
  }

  if (totalQty <= 0) return 0;
  return Number((totalAmount / totalQty).toFixed(2));
}

function toStr(v) {
  return v === null || v === undefined ? "" : String(v);
}
